import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Typography,
} from '@mui/material';
import { BatchApplied, BatchApplyResponse, ReviewBatch, outcomesApi } from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';
import { extractApiError } from '../../utils/apiErrors';
import { CVD_BLUE_TEXT, CVD_ORANGE_TEXT } from '../../utils/cvdPalette';
import {
  BatchEdits, EMAIL_SUBJECT, GROUPS_BEFORE, applyLabel, applyRequest, cadenceChanges, canApply, chosenWeeks,
  countsLine, dayAndTime, emailRowsFromApplied, emailRowsFromPending, emailText, emptyEdits, every, firstName,
  groupsAfter, pending, plural, todayIso,
} from './batch';
import { BatchTable } from './BatchTable';

// Apply a branch's batch at the SYNC (sc-6441 UI AC 3–6): the cadence choice,
// the one table of every item, the Apply bar, the confirmation, and — once
// applied — the email Marah copies and sends herself (the app sends nothing),
// then the read-only table under the applied stamp.

interface Props {
  locationId: number;
  locationName: string;
  batch: ReviewBatch;
  refDay: string; // the review's run_on
  onApplied: (fresh: BatchApplyResponse) => void;
}

type Skipped = BatchApplyResponse['apply_result']['skipped'];

const h2 = { fontSize: 20, fontWeight: 500, mt: 0.5 } as const;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function keptBits(kept: number, left: number): string {
  const bits = [kept ? `${kept} kept with a note` : null, left ? `${left} left at the max now` : null].filter(Boolean);
  return bits.length ? ` ${capitalize(bits.join(', '))}.` : '';
}

const AppliedStamp: React.FC<{ applied: BatchApplied }> = ({ applied }) => {
  const c = applied.counts;
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3ecf8', borderColor: CVD_BLUE_TEXT, display: 'grid', gap: 0.5 }}>
      <Typography>
        <b>Applied {dayAndTime(applied.applied_at)} by {applied.actor_name ?? applied.actor}:</b>{' '}
        {plural(c.max_changes, 'max change')}{c.from_customer ? ` (${c.from_customer} from the customer)` : ''}
        {applied.cadence ? ` and restock ${every(applied.cadence.to)}` : ''}.{keptBits(c.kept, c.left)}
      </Typography>
      <Typography>Nothing is open now. Until the next regeneration, change a max on the inventory page.</Typography>
    </Paper>
  );
};

// The email as Marah will send it: To / Cc (selectable text), Subject, body.
const EmailBox: React.FC<{ recipients: ReviewBatch['recipients']; text: string }> = ({ recipients, text }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50', userSelect: 'text' }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: '2px 10px', fontSize: 13, px: 1.75, py: 1.25,
      borderBottom: 1, borderColor: 'divider', '& > :nth-of-type(odd)': { color: 'text.secondary' } }}>
      {recipients.to ? (
        <>
          <span>To</span><span>{recipients.to}</span>
          <span>Cc</span><span>{recipients.cc.length ? recipients.cc.join(', ') : '—'}</span>
        </>
      ) : (
        <><span>To</span><span>No digest recipients on file.</span></>
      )}
      <span>Subject</span><span>{EMAIL_SUBJECT}</span>
    </Box>
    <Box component="pre" data-testid="email-body" sx={{ m: 0, px: 1.75, py: 1.5, whiteSpace: 'pre-wrap', font: 'inherit',
      fontSize: 13.5, lineHeight: 1.55, maxHeight: '40vh', overflow: 'auto' }}>
      {text}
    </Box>
  </Box>
);

export const EdgeReviewBatch: React.FC<Props> = ({ locationId, locationName, batch, refDay, onApplied }) => {
  const { user } = useAuth();
  const [edits, setEdits] = useState<BatchEdits>(emptyEdits);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [focusNote, setFocusNote] = useState<number | null>(null);
  // confirm -> (Apply) -> applied; an Apply error stays in confirm, with nothing to copy.
  // `applied` is the batch record from Apply's own response, so the email to
  // copy is exactly what was written (never the pre-Apply guess).
  // `open` goes false on Close / Cancel; the content stays until the fade-out
  // ends (onExited), so the title never flips mid-close.
  const [dialog, setDialog] = useState<{
    open: boolean; phase: 'confirm' | 'applied'; busy: boolean; error: string | null; skipped: Skipped;
    applied: BatchApplied | null; copy: 'idle' | 'copied' | 'failed';
  } | null>(null);
  const closeDialog = () => setDialog((d) => d && { ...d, open: false });

  // A fresh review (Regenerate) starts a fresh batch.
  useEffect(() => {
    setEdits(emptyEdits());
    setDrafts({});
    setDialog(null);
  }, [batch.review_id]);

  const p = useMemo(() => pending(batch, edits), [batch, edits]);
  const applied = batch.applied;
  const readOnly = !!applied;
  const weeks = chosenWeeks(batch, edits);

  const setValue = (pe: number, value: number | null) => {
    const row = p.rows.find((r) => r.product_equivalency_id === pe);
    setEdits((e) => {
      const values = { ...e.values };
      if (value == null || value === row?.start) delete values[pe];
      else values[pe] = value;
      return { ...e, values };
    });
  };
  const setDraft = (pe: number, draft: string | null) => setDrafts((d) => {
    const next = { ...d };
    if (draft == null) delete next[pe];
    else next[pe] = draft;
    return next;
  });
  const setIncluded = (pe: number, included: boolean) => {
    setEdits((e) => ({ ...e, unchecked: { ...e.unchecked, [pe]: !included } }));
    setFocusNote(included ? null : pe);
  };

  // The email: before Apply, what Apply will write; after, the batch record.
  const record = dialog?.applied ?? applied;
  const email = record
    ? emailText({ locationName, day: record.applied_on, cadence: record.cadence, rows: emailRowsFromApplied(record),
                  signer: record.actor_name ?? firstName(null, record.actor) })
    : emailText({ locationName, day: todayIso(),
                  cadence: p.cadence ? { from: batch.cadence.current_weeks, to: batch.cadence.recommended_weeks } : null,
                  rows: emailRowsFromPending(p), signer: firstName(user?.name, user?.email) });

  const runApply = async () => {
    setDialog((d) => d && { ...d, busy: true, error: null });
    try {
      const fresh = await outcomesApi.batchApply(locationId, applyRequest(batch, edits));
      onApplied(fresh);
      setDialog({ open: true, phase: 'applied', busy: false, error: null, skipped: fresh.apply_result.skipped,
                  applied: fresh.batch?.applied ?? null, copy: 'idle' });
    } catch (e) {
      setDialog((d) => d && { ...d, busy: false, error: extractApiError(e, 'Apply failed') });
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${EMAIL_SUBJECT}\n\n${email}`);
      setDialog((d) => d && { ...d, copy: 'copied' });
    } catch {
      setDialog((d) => d && { ...d, copy: 'failed' });
    }
  };

  return (
    <Box component="section" sx={{ display: 'grid', gap: 1.25, minWidth: 0 }}>
      {applied ? (
        <>
          <Typography component="h2" sx={h2}>Maxes</Typography>
          <AppliedStamp applied={applied} />
        </>
      ) : (
        <>
          {cadenceChanges(batch) && (
            <>
              <Typography component="h2" sx={h2}>Update cadence</Typography>
              <Paper variant="outlined" sx={{ px: 2, py: 1.25 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={edits.cadenceIn}
                    onChange={(e) => setEdits((x) => ({ ...x, cadenceIn: e.target.checked }))} />}
                  label={`Restock ${every(batch.cadence.recommended_weeks)} instead of ${every(batch.cadence.current_weeks)}`}
                  sx={{ '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                    color: edits.cadenceIn ? 'text.primary' : 'text.disabled',
                    textDecoration: edits.cadenceIn ? 'none' : 'line-through',
                  } }}
                />
                {batch.cadence.median_gap_weeks != null && (
                  <Typography color="text.secondary" sx={{ pl: 4 }}>
                    Typical restock in the last {batch.window_weeks} weeks: {every(batch.cadence.median_gap_weeks)}.
                  </Typography>
                )}
              </Paper>
            </>
          )}
          <Typography component="h2" sx={h2}>Update maxes</Typography>
        </>
      )}

      <BatchTable
        batch={batch}
        rows={p.rows}
        groups={applied ? groupsAfter(applied.applied_on) : GROUPS_BEFORE}
        readOnly={readOnly}
        cadenceWeeks={weeks}
        refDay={refDay}
        drafts={drafts}
        notes={edits.notes}
        focusNote={focusNote}
        onValue={setValue}
        onDraft={setDraft}
        onInclude={setIncluded}
        onNote={(pe, note) => setEdits((e) => ({ ...e, notes: { ...e.notes, [pe]: note } }))}
      />

      {!readOnly && (
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: '10px 20px', alignItems: 'center' }}>
          <Typography color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }}>
            {countsLine(p) || 'No changes pending.'}
          </Typography>
          <Button variant="contained" disabled={!canApply(p)}
            onClick={() => setDialog({ open: true, phase: 'confirm', busy: false, error: null, skipped: [], applied: null,
                                       copy: 'idle' })}>
            {applyLabel(p, batch)}
          </Button>
          {p.missingNotes > 0 && (
            <Typography sx={{ width: '100%', fontSize: 13, color: CVD_ORANGE_TEXT }}>
              Add a note to {p.missingNotes === 1 ? 'the unchecked row' : `each of the ${p.missingNotes} unchecked rows`} first.
            </Typography>
          )}
        </Paper>
      )}

      <Dialog open={!!dialog?.open} onClose={() => !dialog?.busy && closeDialog()} maxWidth="sm" fullWidth
        aria-labelledby="apply-dialog-title" TransitionProps={{ onExited: () => setDialog(null) }}>
        <DialogTitle id="apply-dialog-title">
          {dialog?.phase === 'applied' ? 'Applied. Send this to the branch.' : 'Apply these changes?'}
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.75 }}>
          {dialog?.phase === 'confirm' && (
            <>
              {dialog.error && <Alert severity="error">{dialog.error}</Alert>}
              <Typography>
                This writes {plural(p.writes.length, 'max change')}
                {p.custom ? ` (${p.custom} from the customer)` : ''}
                {p.cadence ? ` and changes the restock to ${every(batch.cadence.recommended_weeks)}` : ''}.
                {keptBits(p.kept.length, p.left.length)}
              </Typography>
              {/* A failed Apply offers nothing to copy. */}
              {!dialog.error && <EmailBox recipients={batch.recipients} text={email} />}
            </>
          )}
          {dialog?.phase === 'applied' && (
            <>
              {dialog.skipped.length > 0 && (
                <Typography color="text.secondary">
                  Skipped {dialog.skipped.length} no longer open:{' '}
                  {dialog.skipped.map((x) => x.name ?? `#${x.recommendation_id}`).join(', ')}.
                </Typography>
              )}
              <EmailBox recipients={batch.recipients} text={email} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {dialog?.phase === 'applied' ? (
            <>
              {dialog.copy !== 'idle' && (
                <Typography color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
                  {dialog.copy === 'copied' ? 'Copied.' : "Couldn't copy. Select the text and copy it."}
                </Typography>
              )}
              <Button variant="contained" onClick={copyEmail}>Copy email</Button>
              <Button onClick={closeDialog}>Close</Button>
            </>
          ) : (
            <>
              <Button onClick={closeDialog} disabled={dialog?.busy}>Cancel</Button>
              <Button variant="contained" onClick={runApply} disabled={dialog?.busy}>
                {dialog?.busy ? 'Applying…' : 'Apply'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
